import {
  type UseComboboxReturnValue, useCombobox, type UseComboboxStateChange, type UseComboboxProps,
} from 'downshift';
import { useTranslation } from 'next-i18next';
import {
  createContext, type ReactNode, type FC, useMemo, useContext,
  useCallback,
  useRef,
} from 'react';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import { useSearchInputContext, type SearchRefs } from 'src/common/components/Search/SearchInputProvider';
import usePlacesAutocomplete from 'src/common/components/Search/usePlacesAutocomplete';
import useSearchUrl from 'src/common/components/Search/useSearchUrl';
import type { AutocompletePrediction } from 'src/common/components/Search/typesGoogle';
import type { RecentSearch, RetrievedRecentSearch } from 'src/common/types/common/RecentSearch';
import mapCategoriesToGroupIds from 'src/search/utils/mapCategoriesToGroupIds';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import { parseISO } from 'date-fns';
import { GEOLOCATION_COOKIE, NEAR_ME } from 'src/common/constants';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import Cookies from 'js-cookie';

type ComboboxItem = AutocompletePrediction | RetrievedRecentSearch;

type SearchComboboxContextType = {
  combobox: UseComboboxReturnValue<ComboboxItem>;
  id: SearchComboboxProviderProps['id'];
  places: AutocompletePrediction[];
  recentSearches: RetrievedRecentSearch[];
  onRecentSearchClick: (search: RecentSearch) => void;
  isFetchingPlaces: boolean;
};

const SearchComboboxContext = createContext<SearchComboboxContextType | undefined>(undefined);

type SearchComboboxProviderProps = {
  /** Downshift associates an input with a menu. Combobox requires id for SSR hydration */
  id: 'modal' | 'hero' | 'navigation' | 'sheet' | 'storybook'; // 'navigation' is SearchNav
  children: ReactNode;
  refs?: Partial<SearchRefs>;
};

/**
 * Combobox items can be either `AutocompletePrediction | RetrievedRecentSearch,`
 * `RetrievedRecentSearch` has a unique key `searchId`.
 */
export const isRecentSearch = (item: ComboboxItem): item is RetrievedRecentSearch => 'searchId' in item;

/**
 * useCombobox must be called above the input and menu it controls, and in same render cycle
 * Alternative might be the `<Downshift >` component
 */
export const SearchComboboxProvider: FC<SearchComboboxProviderProps> = (
  {
    id,
    children,
    refs,
  },
) => {
  const {
    setFilterLocation,
    filterLocation,
    setFilterDates,
    setFilterGuests,
    submitSearch,
    recentSearches: recentSearchesLocalStorage,
  } = useSearchInputContext();
  const modal = useSearchHeroModalContext();
  const { loadSearchUrlFn } = useSearchUrl();
  const {
    loadSdk, searchPlaces, places: autocompletePlaces, isFetching: isFetchingPlaces,
  } = usePlacesAutocomplete();

  const { isMobileNativeWebView } = useMobileNativeWebView();
  const removeNearMeItem = isMobileNativeWebView && Cookies.get(GEOLOCATION_COOKIE) !== 'true';

  const places = useMemo(() => (
    removeNearMeItem
      ? autocompletePlaces.filter((place) => place.place_id !== NEAR_ME)
      : autocompletePlaces
  ), [autocompletePlaces, removeNearMeItem]);

  const recentSearches = useMemo(() => (
    removeNearMeItem
      ? recentSearchesLocalStorage.filter((place) => place.place_id !== NEAR_ME)
      : recentSearchesLocalStorage
  ), [recentSearchesLocalStorage, removeNearMeItem]);

  const { t } = useTranslation('common');

  // TODO: move this to SearchInputProvider
  const onRecentSearchClick = useCallback((search: RecentSearch) => {
    const params = new URLSearchParams();
    const mappedCategories = mapCategoriesToGroupIds(search.categories || []);
    Object.entries(search)
      .filter(([key, value]) => value && (key !== 'locationSlug' && key !== 'searchId' && key !== 'location'))
      .forEach(([key, value]) => {
        if (value) {
          switch (key) {
            case 'dates':
              (value as string[]).forEach(
                (date) => params.append(key, date),
              );
              break;
            case 'categories':
              (mappedCategories).forEach((category) => params.append(key, category));
              break;
            default:
              params.append(key, value as string);
          }
        }
      });

    // Submit form to use existing SearchInput handlers.
    // Also shows selected search in SearchNav as input values
    if (search.place_id) {
      setFilterLocation({
        place_id: search.place_id,
        description: search.location || '',
      });
    }
    if (search.dates) {
      const dates = search.dates.map((date) => parseISO(date));
      setFilterDates(dates);
    }
    if (search.capacity) {
      setFilterGuests(Number(search.capacity));
    }
    submitSearch(params);
    modal.close();
  }, [modal, setFilterDates, setFilterGuests, setFilterLocation, submitSearch]);

  const onSelectAfterRender = useAfterRenderCallback(() => {
    setTimeout(() => (
      refs?.when?.current?.focus() // opens date modal
    ), 100); // both the timeout and afterRender are required for stable selection in Firefox
  });

  // Important: this will trigger a places id lookup on first click on combobox if
  // it is not a nearby location
  const hasSearchedFirstTime = useRef(false);

  const comboboxConfig: UseComboboxProps<ComboboxItem> = useMemo(() => ({
    id,
    initialInputValue: filterLocation?.description,
    selectedItem: filterLocation, // undefined results in "uncontrolled"; rather, init to null
    items: [...places, ...recentSearches],
    isOpen: modal.isLocationOpen,
    itemToString: (item) => {
      if (!item) { return ''; }
      return isRecentSearch(item)
        ? t(`search--description--${item.place_id}`, item.location ?? '') as string
        : t(`search--description--${item.place_id}`, item.description) as string;
    },
    onInputValueChange: (changes) => {
      if (changes.type === useCombobox.stateChangeTypes.InputChange) {
        searchPlaces(changes.inputValue ?? '');
      }
      if (
        changes.type === useCombobox.stateChangeTypes.ItemClick
        || changes.type === useCombobox.stateChangeTypes.FunctionSelectItem
      ) {
        if (!changes.selectedItem) return;
        if (isRecentSearch(changes.selectedItem)) {
          setFilterLocation({
            place_id: changes.selectedItem.place_id,
            description: changes.selectedItem.location ?? '',
          });
        } else {
          setFilterLocation(changes.selectedItem);
        }
      }
    },
    onSelectedItemChange: ({
      selectedItem,
    }: UseComboboxStateChange<ComboboxItem>) => {
      // Selected with mouse/tap
      if (!selectedItem) return;

      if (isRecentSearch(selectedItem)) {
        setFilterLocation({
          place_id: selectedItem.place_id,
          description: selectedItem.location ?? '',
        });

        // Show the page without a modal in the way
        refs?.where?.current?.blur();
        modal.close();
      } else {
        setFilterLocation(selectedItem);
        onSelectAfterRender();
      }
    },
    onStateChange(changes) {
      if (changes.type === useCombobox.stateChangeTypes.InputFocus) {
        loadSearchUrlFn();
        loadSdk();
      }
      if (changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter) {
        // onKeyDownEvent is not emitted from RecentSearc (combobox focus items via aria attributes)
        if (!changes.selectedItem) return;
        if (!isRecentSearch(changes.selectedItem)) return;
        onRecentSearchClick(changes.selectedItem);
      }
      if (
        changes.type === useCombobox.stateChangeTypes.FunctionSelectItem
        && !changes.selectedItem
      ) {
        searchPlaces('');
        setFilterLocation(null);
      }
    },
    onIsOpenChange: (changes) => {
      if (hasSearchedFirstTime.current) return;

      hasSearchedFirstTime.current = true;

      if (!changes.isOpen || !changes?.inputValue) return;
      if (changes.selectedItem?.place_id === NEAR_ME) return;
      if (places.find((place) => changes.selectedItem?.place_id === place.place_id)) return;

      searchPlaces(changes?.inputValue);
    },
  }), [
    id,
    filterLocation,
    places,
    recentSearches,
    modal,
    t,
    searchPlaces,
    setFilterLocation,
    refs?.where,
    onSelectAfterRender,
    loadSearchUrlFn,
    loadSdk,
    onRecentSearchClick,
  ]);

  const combobox = useCombobox<ComboboxItem>(comboboxConfig);

  const contextValue = useMemo(() => ({
    combobox,
    places,
    recentSearches,
    id,
    onRecentSearchClick,
    isFetchingPlaces,
  }), [combobox, id, isFetchingPlaces, onRecentSearchClick, places, recentSearches]);

  return (
    <SearchComboboxContext.Provider value={contextValue}>
      {children}
    </SearchComboboxContext.Provider>
  );
};

export const useSearchComboboxContext = (): SearchComboboxContextType => {
  const context = useContext(SearchComboboxContext);
  if (!context) { throw new Error('useSearchComboboxContext must be used within a SearchComboboxProvider'); }
  return context;
};
